/**
 * Build the display_* query params that the checkout page reads from
 * the URL so it can show the same Cleeng dynamic local-currency total,
 * recurring terms, sub price line, and badge as the landing card. Used
 * by /plan, /promo, /promo/subs3 card components when the user clicks
 * "Pilih Program / Choose Program".
 */
import {
  getCleengCurrentPriceText,
  getCleengPackageExclTaxPriceText,
  getCleengRetailPriceText,
  isDynamicCleengPricing,
} from "./cleengPricing";
import {
  buildCleengCheckoutSubPriceDisplay,
  buildCleengRecurringTermsText,
  selectCleengRedeemSelectedOfferPriceText,
} from "./cleengSummaryPrice";

export function buildCleengCheckoutQuery({
  cleengPrice,
  item,
  pageSource,
  bogoEnabled,
  language,
} = {}) {
  if (!cleengPrice || cleengPrice.source !== "cleeng") {
    return "";
  }

  const params = new URLSearchParams();
  const normalizedPageSource = String(pageSource || "").replace(/^\/+/, "");

  const isRedeemCheckout = normalizedPageSource.indexOf("redeem") === 0;

  // Checkout total must show excl-tax for dynamic Cleeng pricing — otherwise
  // checkout displays incl-tax (e.g. JP 449 instead of 408.18; UK 9 instead
  // of 7.5). Items 6, 11 of Trello WEB card 8 May 26.
  let totalPriceText = isDynamicCleengPricing(cleengPrice)
    ? (getCleengPackageExclTaxPriceText(cleengPrice)
      || getCleengCurrentPriceText(cleengPrice, { discounted: true }))
    : getCleengCurrentPriceText(cleengPrice, { discounted: true });

  const retailPriceText = getCleengRetailPriceText(cleengPrice);
  const redeemSelectedOfferPriceText = selectCleengRedeemSelectedOfferPriceText(cleengPrice, {
    isDynamicCleengPricing: isDynamicCleengPricing(cleengPrice),
    pageSource: normalizedPageSource,
  });

  // Redeem checkout keeps the selected Cleeng charge as its main total. When
  // an offer replaces stale configured pricing, use the same selected-offer
  // resolver as the card; retail remains a separate display-only strike price.
  if (isRedeemCheckout && redeemSelectedOfferPriceText) {
    totalPriceText = redeemSelectedOfferPriceText;
  }

  if (totalPriceText) {
    params.set("display_total_price_text", totalPriceText);
  }

  if (
    retailPriceText
    && retailPriceText !== totalPriceText
  ) {
    params.set("display_retail_price_text", retailPriceText);
  }

  const subPrice = buildCleengCheckoutSubPriceDisplay({
    cleengPrice,
    pageSource,
    validityPeriodAmount: item && item.validity_period_amount,
    bogoEnabled,
    language,
    // Dynamic cleeng: per-person/month should use excl-tax base (item 11).
    excludeTax: isDynamicCleengPricing(cleengPrice),
  });
  if (subPrice) {
    if (subPrice.priceText) {
      params.set("display_sub_price_text", subPrice.priceText);
    }
    if (subPrice.labelText) {
      params.set("display_sub_price_label_text", subPrice.labelText);
    }
  }

  const termsText = buildCleengRecurringTermsText({
    cleengPrice,
    isDynamicCleengPricing: isDynamicCleengPricing(cleengPrice),
    pageSource: normalizedPageSource,
    validityPeriodAmount: item && item.validity_period_amount,
    packageName: item && item.name,
    expectedCurrency: cleengPrice && cleengPrice.currency,
  });
  if (termsText) {
    params.set("display_terms_text", termsText);
  }

  if (item && item.name) {
    params.set("display_name", item.name);
  }

  params.set("display_pricing_source", "cleeng");

  return params.toString();
}
